import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { Helmet } from 'react-helmet-async';
import api from "../services/api"; // import the axios instance
import { useAuth } from "../utils/auth";
import { useNavigate } from "react-router-dom";


const Login = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [passwordStrength, setPasswordStrength] = useState("Very Weak");
  const [loginData, setLoginData] = useState({ email_address: "", password: "" });
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [registerData, setRegisterData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    password: "",
    phone_number: "",
  });
  // const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const checkPasswordStrength = (value) => {
    const hasLetters = /[a-zA-Z]/.test(value);
    const hasNumbers = /[0-9]/.test(value);
    const hasSpecial = /[^a-zA-Z0-9]/.test(value);
    const isLong = value.length >= 8;

    if (hasLetters && hasNumbers && hasSpecial && isLong) {
      setPasswordStrength("Strong");
    } else if ((hasLetters && hasNumbers) || (hasLetters && hasSpecial) || (hasNumbers && hasSpecial)) {
      setPasswordStrength("Medium");
    } else if (value.length >= 6) {
      setPasswordStrength("Weak");
    } else {
      setPasswordStrength("Very Weak");
    }
  };

  const strengthColors = {
    "Very Weak": "bg-red-500 w-1/4",
    Weak: "bg-orange-400 w-2/4",
    Medium: "bg-yellow-400 w-3/4",
    Strong: "bg-green-500 w-full",
  };

  const handleRegister = async () => {
    setError(""); // clear any existing errors
    setSuccess("");

    // Basic validation
    if (!registerData.first_name || !registerData.last_name || !registerData.email_address || !registerData.password) {
      setError("Please fill all required fields.");
      return;
    }

    try {
      await api.post("/api/register", registerData, {
        withCredentials: true,
      });

      setSuccess("Registration successful! You can now login.");
      setRegisterData({
        first_name: "",
        last_name: "",
        email_address: "",
        password: "",
        phone_number: "",
      });
    } catch (err) {
      console.error("Registration failed:", err);
      setError(
        err.response?.data?.detail || "Registration failed. Please try again."
      );
    }
  };

  const handleLogin = async () => {
    try {
      const response = await api.post(
        "/api/login",
        loginData,
        { withCredentials: true } // 🔐 crucial for cookie-based auth
      );


      // If your backend returns CSRF token separately (e.g. in body or header)
      const csrfToken = response.data.csrf_token;
      if (csrfToken) {
        api.defaults.headers.common["X-CSRF-Token"] = csrfToken;
      }
 
      const userRes = await api.get("/api/authenticate", { withCredentials: true });
      setUser(userRes.data);

      // redirect if needed
      navigate("/profile");
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid credentials");
    }
  };


  return (
    <>
      <Helmet>
        <title>Login - Art India Education</title>
      </Helmet>
      <Navbar />
      <main>
        <section className="relative h-screen w-full overflow-hidden font-['EB_Garamond','Nunito','sans-serif']">
          <div className="relative w-full h-full overflow-hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md z-10"></div>
            <img
              src="/login-page-background.jpg"
              alt={`banner`}
              className="absolute top-0 w-full h-full object-cover z-0"
            />

<div className="absolute top-20 md:top-32 left-0 right-0 bottom-0 z-20 flex justify-center items-start overflow-y-auto px-4">
              <div className="bg-black/60 backdrop-blur-md shadow-xl rounded-lg w-full max-w-xl p-8 text-white">
                <div className="flex justify-center mb-6">
                  <button
                    onClick={() => setActiveTab("login")}
                    className={`px-4 py-2 font-bold border-b-2 ${activeTab === "login" ? "border-white text-white" : "border-transparent text-white/70"
                      }`}
                  >
                    LOGIN
                  </button>
                  <button
                    onClick={() => setActiveTab("register")}
                    className={`px-4 py-2 font-bold border-b-2 ml-6 ${activeTab === "register" ? "border-white text-white" : "border-transparent text-white/70"
                      }`}
                  >
                    REGISTER
                  </button>
                </div>

                {activeTab === "login" ? (
                  <div className="space-y-4">
                    <label className="block">
                      <span className="font-semibold">* Username or Email ID</span>
                      <input className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                        placeholder="Enter username or email"
                        value={loginData.username}
                        onChange={(e) => setLoginData({ ...loginData, email_address: e.target.value })}
                      />
                      <p className="text-sm text-red-300 mt-1">Username can not be left blank.</p>

                    </label>

                    <label className="block">
                      <span className="font-semibold">* Password</span>
                      <input
                        type="password"
                        className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                        placeholder="Enter password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      />
                    </label>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" /> Remember me
                      </label>
                      <button className="text-sm text-white/80 hover:underline">Forgot Password</button>
                    </div>
                    <div>
                      {error && <p className="text-red-400 text-sm">{error}</p>}
                      <button onClick={handleLogin}
                        className="bg-[rgb(234,126,93)] text-white w-full text-bold px-4 py-2 rounded hover:bg-opacity-80">Login
                      </button>
                    </div>

                    <div className="text-center text-white/80 mt-4">or login with</div>

                    <div className="flex justify-center space-x-4 mt-2">
                      <button className="bg-blue-600 text-white px-4 py-2 rounded">Google</button>
                      <button className="bg-blue-800 text-white px-4 py-2 rounded">Facebook</button>
                    </div>

                    <p className="text-sm text-center mt-4">
                      Don't have an account? <span className="text-[rgb(234,126,93)] cursor-pointer" onClick={() => setActiveTab("register")}>Register Now</span>
                    </p>
                    <p className="text-sm text-center text-white/80 mt-4">
                      Having trouble registering or signing in? Please contact <a href="mailto:education@artindiamag.com" className="underline">education@artindiamag.com</a>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold text-center mb-4">Create Your Account</h2>

                      {/* First & Last Name */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="block">
                          <span className="font-semibold">* First Name</span>
                          <input
                            className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                            placeholder="First Name"
                            value={registerData.first_name}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, first_name: e.target.value })
                            }
                          />
                        </label>

                        <label className="block">
                          <span className="font-semibold">* Last Name</span>
                          <input
                            className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                            placeholder="Last Name"
                            value={registerData.last_name}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, last_name: e.target.value })
                            }
                          />
                        </label>
                      </div>

                      {/* Email Address */}
                      <label className="block">
                        <span className="font-semibold">* Email Address</span>
                        <input
                          className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                          placeholder="Email"
                          type="email"
                          value={registerData.email_address}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, email_address: e.target.value })
                          }
                        />
                      </label>

                      {/* Password with Strength */}
                      <label className="block">
                        <span className="font-semibold">* Password</span>
                        <input
                          type="password"
                          value={registerData.password}
                          onChange={(e) => {
                            setRegisterData({ ...registerData, password: e.target.value });
                            checkPasswordStrength(e.target.value);
                          }}
                          className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                          placeholder="Create Password"
                        />
                        <div className="mt-1 h-2 w-full bg-white/20 rounded">
                          <div
                            className={`h-2 rounded transition-all duration-300 ${strengthColors[passwordStrength]}`}
                          ></div>
                        </div>
                        <p className="text-sm text-white/70 mt-1">Strength: {passwordStrength}</p>
                      </label>

                      {/* Phone Number */}
                      <label className="block">
                        <span className="font-semibold">* Phone Number</span>
                        <input
                          className="w-full p-2 mt-1 border rounded bg-white/20 text-white placeholder-white/80"
                          placeholder="Phone Number"
                          value={registerData.phone_number}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, phone_number: e.target.value })
                          }
                        />
                      </label>
              
                    <div>
                      {error && <p className="text-red-400 text-sm">{error}</p>}
                      {success && <p className="text-green-400 text-sm">{success}</p>}
                      <button onClick={handleRegister}
                        className="bg-[rgb(234,126,93)] text-white w-full text-bold px-4 py-2 rounded hover:bg-opacity-80">Register
                      </button>
                    </div>

                    <p className="text-sm text-center mt-4">
                      Already have an account? <span className="text-[rgb(234,126,93)] cursor-pointer" onClick={() => setActiveTab("login")}>Login</span>
                    </p>
                    <p className="text-sm text-center text-white/80 mt-4">
                      Having trouble registering or signing in? Please contact <a href="mailto:education@artindiamag.com" className="underline">education@artindiamag.com</a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

    </>
  );
};

export default Login;

